import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './i18n';
import './index.css';
import { AuthProvider, useAuth } from './auth-context';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Grades } from './pages/Grades';
import { Subjects } from './pages/Subjects';
import { Book } from './pages/Book';
import { Watch } from './pages/Watch';
import { AdminSchools } from './pages/AdminSchools';
import { AdminSchoolNew } from './pages/AdminSchoolNew';
import { AdminSchoolEdit } from './pages/AdminSchoolEdit';
import { AdminUsage } from './pages/AdminUsage';
import { AdminSchoolUsage } from './pages/AdminSchoolUsage';
import { SchoolUsers } from './pages/SchoolUsers';
import { CoordinatorTeachers } from './pages/CoordinatorTeachers';
import { Account } from './pages/Account';

function Protected({ children }: { children: React.ReactNode }) {
  const { me, ready } = useAuth();
  if (!ready) return null;
  if (!me) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Super-admin routes. This only hides the screens — every /api/admin route is
 * SUPER_ADMIN-guarded server-side, which is the boundary that actually counts.
 * A non-admin who types the URL lands back on the catalog rather than a denial,
 * since for them the page simply does not exist.
 */
function SuperAdmin({ children }: { children: React.ReactNode }) {
  const { me, ready } = useAuth();
  if (!ready) return null;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'SUPER_ADMIN') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * School-admin routes (Phase 3). Same deal as SuperAdmin above: cosmetic. Every
 * /api/school route is SCHOOL_ADMIN-guarded and scoped to the caller's own
 * school server-side. A super admin landing here is sent to their own console
 * rather than the catalog, since /api/school would 403 for them.
 */
function SchoolAdmin({ children }: { children: React.ReactNode }) {
  const { me, ready } = useAuth();
  if (!ready) return null;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role === 'SUPER_ADMIN') return <Navigate to="/admin/schools" replace />;
  if (me.role !== 'SCHOOL_ADMIN') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Coordinator routes (Phase 4). Cosmetic in exactly the same way: every
 * /api/coordinator route is COORDINATOR-guarded and narrowed to the caller's
 * own teachers server-side. Each higher role is sent to its own console rather
 * than the catalog, since /api/coordinator would 403 for them.
 */
function Coordinator({ children }: { children: React.ReactNode }) {
  const { me, ready } = useAuth();
  if (!ready) return null;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role === 'SUPER_ADMIN') return <Navigate to="/admin/schools" replace />;
  if (me.role === 'SCHOOL_ADMIN') return <Navigate to="/school/users" replace />;
  if (me.role !== 'COORDINATOR') return <Navigate to="/" replace />;
  return <>{children}</>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Signed-out, like /login. The reset link in the email lands on
              /reset?token=… — note the catch-all below would otherwise
              bounce it to / and drop the token. */}
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/" element={<Protected><Grades /></Protected>} />
          <Route path="/grade/:id" element={<Protected><Subjects /></Protected>} />
          <Route path="/book/:id" element={<Protected><Book /></Protected>} />
          <Route path="/watch/:id" element={<Protected><Watch /></Protected>} />
          {/* Phase 8. Protected, not role-gated: the page only ever acts on the
              caller's own account, and both endpoints take the user id from the
              token, so there is nothing here for a role to widen or narrow. */}
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/admin" element={<Navigate to="/admin/schools" replace />} />
          <Route path="/admin/schools" element={<SuperAdmin><AdminSchools /></SuperAdmin>} />
          <Route path="/admin/schools/new" element={<SuperAdmin><AdminSchoolNew /></SuperAdmin>} />
          <Route path="/admin/schools/:id" element={<SuperAdmin><AdminSchoolEdit /></SuperAdmin>} />
          {/* Phase 7 usage views. Guarded here only cosmetically — the
              /api/admin/usage/* routes are SUPER_ADMIN-only server-side, so a
              school admin who types the URL gets no data either way. */}
          <Route path="/admin/usage" element={<SuperAdmin><AdminUsage /></SuperAdmin>} />
          <Route path="/admin/usage/:id" element={<SuperAdmin><AdminSchoolUsage /></SuperAdmin>} />
          <Route path="/school" element={<Navigate to="/school/users" replace />} />
          <Route path="/school/users" element={<SchoolAdmin><SchoolUsers /></SchoolAdmin>} />
          <Route path="/coordinator" element={<Navigate to="/coordinator/teachers" replace />} />
          <Route
            path="/coordinator/teachers"
            element={<Coordinator><CoordinatorTeachers /></Coordinator>}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
